import React, { CSSProperties, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Alert, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { VANITY_ITEM_OWNERSHIP_STATS, VANITY_ITEM_SPEND_TIER_USERS } from './graphql';

type ScopeKey = 'TOTAL' | 'ACTIVE';
type TierChip = { key: string; tier: string; label: string; bg: string; title: string };

// Ordered high-spend → low, then non-payers, then the >$1M outlier. Each renders as a colored
// pill only when it has at least one user; clicking it lists that tier's users. `tier` is the
// GraphQL VanitySpendTier enum value.
const SPEND_TIER_CHIPS: TierChip[] = [
  { key: 'kraken', tier: 'KRAKEN', label: 'Kraken', bg: '#7b1fa2', title: 'USD 1,000 – 1,000,000' },
  { key: 'whale', tier: 'WHALE', label: 'Whale', bg: '#1565c0', title: 'USD 100 – 1,000' },
  { key: 'dolphin', tier: 'DOLPHIN', label: 'Dolphin', bg: '#0097a7', title: 'USD 20 – 100' },
  { key: 'minnow', tier: 'MINNOW', label: 'Minnow', bg: '#2e7d32', title: 'USD 0 – 20' },
  { key: 'npau', tier: 'NPAU', label: 'Non-payer', bg: '#616161', title: 'No spend' },
  { key: 'uncategorized', tier: 'UNCATEGORIZED', label: '> 1M', bg: '#c62828', title: 'USD > 1,000,000' },
];

const PILL_STYLE: CSSProperties = {
  color: '#fff',
  padding: '1px 8px',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const DEFAULT_ACTIVE_DAYS = 30;
const USERS_PAGE_SIZE = 200;

export type VanityCategory = 'STICKER' | 'APP_SKIN' | 'PROFILE_FRAME' | 'POSTER' | 'BADGE' | 'EMOJI_PACK';

type Props = {
  category: VanityCategory;
  itemId: string;
};

const OwnershipStatsCard = ({ category, itemId }: Props) => {
  const baseId = itemId.split('.')[0];
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeDays, setActiveDays] = useState(DEFAULT_ACTIVE_DAYS);

  const [runQuery, { data, loading, error, called }] = useLazyQuery(VANITY_ITEM_OWNERSHIP_STATS, {
    fetchPolicy: 'network-only',
  });

  const stats = data?.vanityItemOwnershipStats;
  const stale = stats && (stats.vanityItemIdPrefix !== baseId || stats.category !== category);
  // Window the displayed result was computed for (falls back to the input while loading).
  const windowDays = stats && !stale ? stats.activeDays : activeDays;
  const spendTiers = stats && !stale ? stats.spendTiers : null;
  const validDays = Number.isInteger(activeDays) && activeDays >= 1 && activeDays <= 30;

  const handleConfirm = () => {
    setShowConfirm(false);
    runQuery({ variables: { category, vanityItemIdPrefix: baseId, activeDays } });
  };

  // --- Spend-tier user list (modal) ---
  const [modalTier, setModalTier] = useState<{ scope: ScopeKey; chip: TierChip } | null>(null);
  const [tierUsers, setTierUsers] = useState<{ userId: string; name: string | null }[]>([]);
  const [tierUsersTotal, setTierUsersTotal] = useState(0);
  const [tierUsersHasMore, setTierUsersHasMore] = useState(false);

  const [runTierUsers, { loading: usersLoading, error: usersError }] = useLazyQuery(VANITY_ITEM_SPEND_TIER_USERS, {
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      const r = d?.vanityItemSpendTierUsers;
      if (!r) {
        return;
      }
      setTierUsers((prev) => [...prev, ...r.users]);
      setTierUsersTotal(r.total);
      setTierUsersHasMore(r.hasMore);
    },
  });

  const openTierUsers = (scope: ScopeKey, chip: TierChip) => {
    setModalTier({ scope, chip });
    setTierUsers([]);
    setTierUsersTotal(0);
    setTierUsersHasMore(false);
    runTierUsers({
      variables: {
        category,
        vanityItemIdPrefix: baseId,
        activeDays: windowDays,
        scope,
        tier: chip.tier,
        limit: USERS_PAGE_SIZE,
        offset: 0,
      },
    });
  };

  const loadMoreTierUsers = () => {
    if (!modalTier) {
      return;
    }
    runTierUsers({
      variables: {
        category,
        vanityItemIdPrefix: baseId,
        activeDays: windowDays,
        scope: modalTier.scope,
        tier: modalTier.chip.tier,
        limit: USERS_PAGE_SIZE,
        offset: tierUsers.length,
      },
    });
  };

  const renderTierChips = (counts: Record<string, number> | null | undefined, scope: ScopeKey) => {
    if (!counts) {
      return null;
    }
    const chips = SPEND_TIER_CHIPS.filter((c) => Number(counts[c.key] ?? 0) > 0);
    if (chips.length === 0) {
      return null;
    }
    return (
      <span className="d-inline-flex flex-wrap align-items-center" style={{ gap: 6, marginLeft: 8 }}>
        {chips.map((c) => (
          <span
            key={c.key}
            role="button"
            tabIndex={0}
            title={`${c.title} — click to list users`}
            onClick={() => openTierUsers(scope, c)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openTierUsers(scope, c);
              }
            }}
            style={{ ...PILL_STYLE, backgroundColor: c.bg }}
          >
            {c.label}: {Number(counts[c.key]).toLocaleString()}
          </span>
        ))}
      </span>
    );
  };

  return (
    <Card className="mt-3">
      <Card.Header>
        <span className="font-weight-bold">
          <i className="fas fa-users mr-2" />
          Vanity Ownership Stats
        </span>
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="py-2">
          <i className="fas fa-info-circle mr-2" />
          Counts how many users own this item (any level), how many have been active in the last {windowDays} days,
          and splits each group by USD lifetime spend tier. Click a tier pill to list its users.
        </Alert>

        <Form.Group className="mb-3" style={{ maxWidth: 260 }}>
          <Form.Label className="font-weight-bold mb-1">Active window (days)</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={30}
            value={activeDays}
            isInvalid={!validDays}
            disabled={loading}
            onChange={(e) => setActiveDays(parseInt(e.target.value, 10))}
          />
          <Form.Control.Feedback type="invalid">
            Enter a whole number of days between 1 and 30.
          </Form.Control.Feedback>
        </Form.Group>

        <Table bordered size="sm" className="mb-2">
          <tbody>
            <tr>
              <th style={{ width: 220 }}>Item ID</th>
              <td>
                <code>{baseId}</code>
              </td>
            </tr>
            <tr>
              <th>Total users owning</th>
              <td>
                {loading ? (
                  <span className="text-muted">
                    <Spinner as="span" animation="border" size="sm" className="mr-2" />
                    Counting...
                  </span>
                ) : stats && !stale ? (
                  <span className="d-inline-flex align-items-center flex-wrap">
                    {stats.totalUsers.toLocaleString()}
                    {renderTierChips(spendTiers?.total, 'TOTAL')}
                  </span>
                ) : (
                  <span className="text-muted">Not yet counted</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Active in last {windowDays} days</th>
              <td>
                {loading ? (
                  <span className="text-muted">
                    <Spinner as="span" animation="border" size="sm" className="mr-2" />
                    Counting...
                  </span>
                ) : stats && !stale ? (
                  <span className="d-inline-flex align-items-center flex-wrap">
                    {stats.activeUsers.toLocaleString()}
                    {renderTierChips(spendTiers?.active, 'ACTIVE')}
                  </span>
                ) : (
                  <span className="text-muted">Not yet counted</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Computed at</th>
              <td>
                {loading ? (
                  <span className="text-muted">—</span>
                ) : stats && !stale ? (
                  new Date(stats.computedAt).toLocaleString()
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          </tbody>
        </Table>

        {stats && !stale && spendTiers?.spendComputedAt && (
          <div className="text-muted small mb-2">
            Spend tiers as of {new Date(spendTiers.spendComputedAt).toLocaleString()} (USD lifetime spend).
          </div>
        )}
        {stats && !stale && !spendTiers && (
          <div className="text-muted small mb-2">
            Spend breakdown unavailable (too many owners for a breakdown, or spend data not present on this
            environment).
          </div>
        )}

        {error && (
          <Alert variant="danger" className="py-2">
            {error.message}
          </Alert>
        )}

        <Alert variant="warning" className="py-2">
          <i className="fas fa-exclamation-triangle mr-2" />
          <b>Expensive operation</b> (may take up to a minute) — please run only when needed.
        </Alert>
        <Button onClick={() => setShowConfirm(true)} disabled={loading || !itemId || !validDays} variant="primary">
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="mr-2" />
              Running (this can take a minute)...
            </>
          ) : called ? (
            'Re-run Stats'
          ) : (
            'Get Stats'
          )}
        </Button>
      </Card.Body>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-warning mr-2" />
            Heads up — expensive operation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This is expensive on the database (may take up to a minute). Please run only when needed — once per item is
          usually enough.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Run anyway
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!modalTier} onHide={() => setModalTier(null)} centered scrollable size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalTier && (
              <span
                style={{ ...PILL_STYLE, cursor: 'default', backgroundColor: modalTier.chip.bg, marginRight: 8 }}
              >
                {modalTier.chip.label}
              </span>
            )}
            {modalTier?.scope === 'ACTIVE' ? `Active owners (last ${windowDays}d)` : 'All owners'}
            {tierUsersTotal > 0 && <span className="text-muted"> — {tierUsersTotal.toLocaleString()} users</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {usersError && <Alert variant="danger">{usersError.message}</Alert>}
          {tierUsers.length > 0 && (
            <>
              <div className="text-muted small mb-2">
                Showing {tierUsers.length.toLocaleString()} of {tierUsersTotal.toLocaleString()} — click a user to
                open it in User View.
              </div>
              <ol className="mb-2" style={{ paddingLeft: 20, fontSize: 13 }}>
                {tierUsers.map((u) => (
                  <li key={u.userId}>
                    <a href={`/sp3/devtools/userview/${u.userId}`} target="_blank" rel="noreferrer">
                      {u.name || '(no name)'}
                    </a>{' '}
                    <span className="text-muted" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {u.userId}
                    </span>
                  </li>
                ))}
              </ol>
            </>
          )}
          {usersLoading && (
            <div className="text-muted">
              <Spinner as="span" animation="border" size="sm" className="mr-2" />
              Loading users...
            </div>
          )}
          {!usersLoading && tierUsers.length === 0 && !usersError && <div className="text-muted">No users.</div>}
        </Modal.Body>
        <Modal.Footer>
          {tierUsersHasMore && (
            <Button variant="outline-primary" disabled={usersLoading} onClick={loadMoreTierUsers}>
              {usersLoading ? 'Loading...' : `Load more (${USERS_PAGE_SIZE})`}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setModalTier(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default OwnershipStatsCard;
